import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PortalNavbarComponent } from './portal-navbar.component';
import {
  DataHealthAnomalyDto,
  DataHealthDashboardDto,
  DataHealthService,
  DataHealthSummaryDto,
  DataHealthTrendPointDto
} from '../services/data-health.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-data-health-dashboard-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/home" class="home-btn">Home</a>
          <h1 class="page-title">Utilities · Data Health Dashboard</h1>
          <p class="page-sub">Health check results for data integrity and consistency.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Dashboard Filters</span>
              <div class="card-tools">
                <select class="tool-select" [(ngModel)]="selectedEntityType" (ngModelChange)="loadDashboard()">
                  @for (entityType of entityTypes; track entityType) {
                    <option [ngValue]="entityType">{{ entityType }}</option>
                  }
                </select>
                @if (isSiteAdmin()) {
                  <select class="tool-select" [(ngModel)]="selectedOrganizationId" (ngModelChange)="loadDashboard()">
                    <option [ngValue]="0">All Organizations</option>
                    @for (org of availableOrganizations(); track org.organizationId) {
                      <option [ngValue]="org.organizationId">{{ org.organizationName }}</option>
                    }
                  </select>
                }
              </div>
            </div>

            <div class="metrics-grid">
              <div class="metric">
                <div class="metric-label">Summary Rows</div>
                <div class="metric-value">{{ summary().length }}</div>
              </div>
              <div class="metric">
                <div class="metric-label">Total Records</div>
                <div class="metric-value">{{ totalRecords() }}</div>
              </div>
              <div class="metric">
                <div class="metric-label">Total Anomalies</div>
                <div class="metric-value">{{ totalAnomalies() }}</div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Latest Summary</span>
              <span class="card-note">{{ summary().length }} item{{ summary().length !== 1 ? 's' : '' }}</span>
            </div>
            <div class="table-wrap">
              <div class="table-head">
                <span>Organization</span>
                <span>Entity</span>
                <span>Records</span>
                <span>Anomalies</span>
                <span>Run Time (UTC)</span>
              </div>
              @for (item of summary(); track trackSummary(item)) {
                <div class="table-row">
                  <span>{{ item.organizationName }}</span>
                  <span>{{ item.entityType }}</span>
                  <span>{{ item.latestRecordCount }}</span>
                  <span>{{ item.latestAnomalyCount }}</span>
                  <span>{{ formatUtc(item.runAtUtc) }}</span>
                </div>
              } @empty {
                <div class="empty">No summary data available yet.</div>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Trend Points</span>
              <span class="card-note">{{ trends().length }} point{{ trends().length !== 1 ? 's' : '' }}</span>
            </div>
            <div class="table-wrap">
              <div class="table-head">
                <span>Entity</span>
                <span>Organization</span>
                <span>Run Time (UTC)</span>
                <span>Records</span>
                <span>Anomalies</span>
              </div>
              @for (item of trends(); track trackTrend(item)) {
                <div class="table-row">
                  <span>{{ item.entityType }}</span>
                  <span>{{ resolveOrgName(item.organizationId) }}</span>
                  <span>{{ formatUtc(item.runAtUtc) }}</span>
                  <span>{{ item.recordCount }}</span>
                  <span>{{ item.anomalyCount }}</span>
                </div>
              } @empty {
                <div class="empty">No trend data available yet.</div>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Anomaly Drill-down</span>
              <span class="card-note">{{ anomalies().length }} item{{ anomalies().length !== 1 ? 's' : '' }}</span>
            </div>
            <div class="table-wrap">
              <div class="table-head">
                <span>Entity</span>
                <span>Organization</span>
                <span>Code</span>
                <span>Summary</span>
                <span>Potential Cause</span>
                <span>Detected (UTC)</span>
              </div>
              @for (item of anomalies(); track item.id) {
                <div class="table-row table-row--wide">
                  <span>{{ item.entityType }}</span>
                  <span>{{ item.organizationName }}</span>
                  <span>{{ item.anomalyCode }}</span>
                  <span>{{ item.summary }}</span>
                  <span>{{ item.potentialCause || '—' }}</span>
                  <span>{{ formatUtc(item.detectedAtUtc) }}</span>
                </div>
              } @empty {
                <div class="empty">No anomalies detected for selected filters.</div>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { min-height: 100vh; }
    .page-hero {
      border-bottom: 1px solid var(--color-border);
      padding: 2rem 1.5rem;
      background: linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
    }
    .page-hero-inner, .page-body-inner { max-width: 1180px; margin: 0 auto; }
    .home-btn { color: var(--color-muted); text-decoration: none; }
    .page-title { margin: .6rem 0 0; }
    .page-sub { margin: .4rem 0 0; color: var(--color-muted); }
    .page-body { padding: 1.5rem; display: grid; gap: .9rem; }
    .status-banner {
      margin-bottom: .8rem;
      border: 1px solid var(--color-border);
      border-radius: .6rem;
      padding: .55rem .75rem;
    }
    .card {
      border: 1px solid var(--color-border);
      border-radius: .9rem;
      overflow: hidden;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
      margin-bottom: .9rem;
    }
    .card-hd {
      display: flex;
      justify-content: space-between;
      gap: .6rem;
      flex-wrap: wrap;
      border-bottom: 1px solid var(--color-border);
      padding: .8rem 1rem;
    }
    .card-title { font-weight: 600; }
    .card-note { color: var(--color-muted); font-size: .85rem; }
    .card-tools { display: flex; gap: .45rem; flex-wrap: wrap; }
    .tool-select {
      border: 1px solid var(--color-border);
      border-radius: .45rem;
      background: transparent;
      color: var(--color-text);
      padding: .4rem .55rem;
    }
    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: .75rem;
      padding: .9rem 1rem;
    }
    .metric {
      border: 1px solid var(--color-border);
      border-radius: .6rem;
      padding: .75rem;
      color: var(--color-text);
    }
    .metric-label { color: var(--color-muted); font-size: .78rem; }
    .metric-value { font-size: 1.3rem; font-weight: 700; margin-top: .2rem; }
    .table-wrap { display: flex; flex-direction: column; }
    .table-head,.table-row {
      display: grid;
      grid-template-columns: 1.2fr .9fr .8fr .8fr 1fr;
      gap: .7rem;
      align-items: center;
      padding: .7rem 1rem;
    }
    .table-row--wide {
      grid-template-columns: .8fr 1fr .9fr 1.5fr 1.5fr 1fr;
    }
    .table-head {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      font-size: .72rem;
      letter-spacing: .02em;
      text-transform: uppercase;
      font-weight: 600;
    }
    .table-row { border-bottom: 1px solid color-mix(in srgb, var(--color-border) 50%, transparent); }
    .table-row:last-child { border-bottom: none; }
    .empty { padding: 1rem; color: var(--color-muted); }
  `]
})
export class DataHealthDashboardPageComponent implements OnInit {
  private readonly dataHealthService = inject(DataHealthService);
  private readonly authService = inject(AuthService);

  readonly dashboard = signal<DataHealthDashboardDto>({ summary: [], trends: [] });
  readonly anomalies = signal<DataHealthAnomalyDto[]>([]);
  readonly message = signal<string>('');
  readonly isSiteAdmin = signal(false);

  readonly summary = computed(() => this.dashboard().summary);
  readonly trends = computed(() => this.dashboard().trends);
  readonly totalRecords = computed(() => this.summary().reduce((sum, item) => sum + item.latestRecordCount, 0));
  readonly totalAnomalies = computed(() => this.summary().reduce((sum, item) => sum + item.latestAnomalyCount, 0));

  entityTypes = ['All', 'Events', 'Teams', 'Players', 'Questions', 'Organizations'];
  selectedEntityType = 'All';
  selectedOrganizationId = 0;

  ngOnInit(): void {
    void this.initializeAsync();
  }

  availableOrganizations(): DataHealthSummaryDto[] {
    const seen = new Set<number>();
    const rows: DataHealthSummaryDto[] = [];

    for (const item of this.summary()) {
      if (seen.has(item.organizationId)) {
        continue;
      }

      seen.add(item.organizationId);
      rows.push(item);
    }

    return rows.sort((a, b) => a.organizationName.localeCompare(b.organizationName));
  }

  loadDashboard(): void {
    const orgFilter = this.isSiteAdmin() ? this.selectedOrganizationId : undefined;
    this.dataHealthService.getDashboard(this.selectedEntityType, orgFilter).subscribe({
      next: payload => {
        this.dashboard.set(payload);
        this.loadAnomalies();
      },
      error: () => this.message.set('Unable to load dashboard data.')
    });
  }

  loadAnomalies(): void {
    const orgFilter = this.isSiteAdmin() ? this.selectedOrganizationId : undefined;
    this.dataHealthService.getAnomalies(this.selectedEntityType, orgFilter).subscribe({
      next: rows => this.anomalies.set(rows),
      error: () => this.message.set('Unable to load anomaly details.')
    });
  }

  resolveOrgName(organizationId: number): string {
    return this.summary().find(s => s.organizationId === organizationId)?.organizationName || `Org ${organizationId}`;
  }

  formatUtc(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  }

  trackSummary(item: DataHealthSummaryDto): string {
    return `${item.organizationId}-${item.entityType}`;
  }

  trackTrend(item: DataHealthTrendPointDto): string {
    return `${item.organizationId}-${item.entityType}-${item.runAtUtc}`;
  }

  private async initializeAsync(): Promise<void> {
    const roles = await this.authService.getRolesAsync();
    this.isSiteAdmin.set(roles.includes('SiteAdmin'));
    this.loadDashboard();
  }
}
